import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription, interval } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  CountriesQuery,
  VitaCountry,
  VitaDiagnostics,
  VitaHealth,
  VitaPricesResponse,
  VitaWalletItem,
  VitaWalletService,
} from '../../services/vita-wallet.service';
import { AddCountryDialogComponent } from './add-country-dialog/add-country-dialog.component';
import { ConfirmDeactivateCountryDialogComponent } from './confirm-deactivate-country-dialog/confirm-deactivate-country-dialog.component';
import { SimulateRateDialogComponent } from './simulate-rate-dialog/simulate-rate-dialog.component';
import { DiagnosticsDialogComponent } from './diagnostics-dialog/diagnostics-dialog.component';
import { ConvertCurrencyDialogComponent } from './convert-currency-dialog/convert-currency-dialog.component';
import { RailColombiaConfigComponent } from '../rail-colombia-config/rail-colombia-config.component';
import { RailPeruConfigComponent } from '../rail-peru-config/rail-peru-config.component';
import { RailVesPenConfigComponent } from '../rail-ves-pen-config/rail-ves-pen-config.component';
import { RailClpPenConfigComponent } from '../rail-clp-pen-config/rail-clp-pen-config.component';
import { RailPeruClientSimComponent } from '../rail-peru-client-sim/rail-peru-client-sim.component';
import { RailClpCopConfigComponent } from '../rail-clp-cop-config/rail-clp-cop-config.component';
import { RailVesCopConfigComponent } from '../rail-ves-cop-config/rail-ves-cop-config.component';
import { RailColombiaClientSimComponent } from '../rail-colombia-client-sim/rail-colombia-client-sim.component';
import { buildVitaVariantLabel } from '../../utils/vita-variant-label.util';

interface WithdrawalRow {
  sourceCurrency: string;
  countryCode: string;
  sellPrice: number;
  fixedCost: number;
  fixedCostUsd: number;
  minAmount: number;
  businessDays: number;
  // Precomputed
  baseCountry: string;
  flagIso: string | null;
  destLabel: string;
  destCurrency: string;
  exampleAmount: number;
  exampleReceive: number;
}

interface VitaSentRow {
  sourceCurrency: string;
  countryCode: string;
  sellPrice: number;
  fixedCost: number;
  // Precomputed
  baseCountry: string;
  flagIso: string | null;
  destLabel: string;
  destCurrency: string;
  exampleAmount: number;
  exampleReceive: number;
}

interface PayinRow {
  countryCode: string;
  paymentMethod: string;
  sourceCurrency: string;
  destinationCurrency: string;
  sellPrice: number;
  fixedCost: number;
  spread: number;
  spreadType: string;
  // Precomputed
  baseCountry: string;
  flagIso: string | null;
  destLabel: string;
  exampleAmount: number;
  exampleReceive: number;
}

interface BalanceRow {
  currency: string;
  amount: number;
}

interface WalletView {
  item: VitaWalletItem;
  balances: BalanceRow[];
}

type ActiveFilter = '' | 'true' | 'false';

@Component({
  selector: 'app-vita-dashboard',
  templateUrl: './vita-dashboard.component.html',
  styleUrls: ['./vita-dashboard.component.sass'],
})
export class VitaDashboardComponent implements OnInit, OnDestroy {
  // Service master toggle (VITA_SERVICIO)
  serviceEnabled = true;
  serviceLoading = false;
  serviceToggling = false;

  // Health
  health: VitaHealth | null = null;
  healthLoading = true;
  healthError: string | null = null;

  // Wallets
  wallets: VitaWalletItem[] = [];
  walletViews: WalletView[] = [];
  walletsLoading = true;
  walletsError: string | null = null;

  // Health derived (cached — evitar getters en template)
  isOk = false;
  environmentLabel = '—';
  latencyClass = 'latency-ok';

  expandedSections: Set<string> = new Set([
    'health',
    'balances',
    'catalog',
    'prices',
  ]);

  // Countries state (whitelist de países permitidos)
  countries: VitaCountry[] = [];
  countriesLoading = false;
  countriesError: string | null = null;
  countriesTotal = 0;
  countriesPage = 1;
  countriesLimit = 20;
  countriesTotalPages = 1;
  countriesSearch = '';
  countriesActive: ActiveFilter = '';
  togglingCountryId: number | null = null;
  deletingCountryId: number | null = null;
  diagnosticsLoading = false;

  // Lookup: active countries (para selectores del dashboard)
  countryOptions: VitaCountry[] = [];

  // Prices state
  pricesRaw: VitaPricesResponse | null = null;
  pricesLoading = false;
  pricesError: string | null = null;
  pricesFetchedAt: Date | null = null;
  pricesValidUntil: Date | null = null;

  pricesTab: 'withdrawal' | 'vitasent' | 'payins' = 'withdrawal';

  withdrawalRows: WithdrawalRow[] = [];
  vitaSentRows: VitaSentRow[] = [];
  payinRows: PayinRow[] = [];

  // Filtered views — cached, se recalculan solo cuando cambian filtros o datos
  filteredWithdrawalRows: WithdrawalRow[] = [];
  filteredVitaSentRows: VitaSentRow[] = [];
  filteredPayinRows: PayinRow[] = [];

  pricesCountryFilter = '';
  pricesSourceCurrencyFilter = '';
  pricesPaymentMethodFilter = '';
  pricesSearch = '';

  // Monto de ejemplo para simular envío/recibido en cada fila
  exampleAmount = 100000;

  // Opciones derivadas del payload
  sourceCurrencyOptions: string[] = [];
  paymentMethodOptions: string[] = [];

  private sub = new Subscription();
  private countrySearch$ = new Subject<string>();

  constructor(
    private vita: VitaWalletService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.loadServiceStatus();
    this.loadHealth();
    this.loadWallets();
    this.loadCountryOptions();
    this.loadCountries();
    this.loadPrices();

    // Polling cada 5 minutos (health + precios)
    this.sub.add(
      interval(300_000).subscribe(() => this.loadHealth()),
    );
    this.sub.add(
      interval(300_000).subscribe(() => this.loadPrices(false)),
    );

    this.sub.add(
      this.countrySearch$
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe(() => {
          this.countriesPage = 1;
          this.loadCountries();
        }),
    );

  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  // -------------- Service status (VITA_SERVICIO) --------------

  loadServiceStatus(): void {
    this.serviceLoading = true;
    this.sub.add(
      this.vita.getServiceStatus().subscribe({
        next: (res) => {
          this.serviceEnabled = !!res?.enabled;
          this.serviceLoading = false;
        },
        error: (err) => {
          if (!err?.transient) {
            console.error('Error leyendo VITA_SERVICIO', err);
          }
          this.serviceLoading = false;
        },
      }),
    );
  }

  toggleServiceStatus(): void {
    if (this.serviceToggling) return;
    this.serviceToggling = true;
    const next = !this.serviceEnabled;
    this.sub.add(
      this.vita.toggleServiceStatus(next).subscribe({
        next: (res) => {
          this.serviceEnabled = !!res?.enabled;
          this.serviceToggling = false;
        },
        error: () => {
          this.serviceToggling = false;
        },
      }),
    );
  }

  // -------------- Health --------------

  loadHealth(): void {
    this.healthError = null;
    this.sub.add(
      this.vita.getHealth().subscribe({
        next: (h) => {
          this.health = h;
          this.isOk = h?.status === 'ok';
          this.environmentLabel = h?.environment?.toUpperCase() || '—';
          const ms = h?.latencyMs ?? 0;
          this.latencyClass =
            ms > 1500 ? 'latency-danger' : ms > 700 ? 'latency-warning' : 'latency-ok';
          this.healthLoading = false;
        },
        error: (err) => {
          // 401/403 transiente → ignorar, próximo tick vuelve a intentar
          if (err?.transient) {
            this.healthLoading = false;
            return;
          }
          this.healthError =
            typeof err === 'string' ? err : 'Error consultando estado de Vita';
          this.healthLoading = false;
        },
      }),
    );
  }

  // -------------- Wallets --------------

  loadWallets(): void {
    this.walletsLoading = true;
    this.walletsError = null;
    this.sub.add(
      this.vita.listWallets(1, 50).subscribe({
        next: (res) => {
          this.wallets = res?.wallets || [];
          this.walletViews = this.wallets.map((w) => ({
            item: w,
            balances: this.computeBalanceRows(w),
          }));
          this.walletsLoading = false;
        },
        error: (err) => {
          if (err?.transient) {
            this.walletsLoading = false;
            return; // silent retry next tick
          }
          this.wallets = [];
          this.walletViews = [];
          this.walletsError =
            typeof err === 'string' ? err : 'Error obteniendo saldos';
          this.walletsLoading = false;
        },
      }),
    );
  }

  private computeBalanceRows(wallet: VitaWalletItem): BalanceRow[] {
    const balances = wallet?.attributes?.balances || {};
    return Object.entries(balances)
      .map(([currency, amount]) => ({
        currency: currency.toUpperCase(),
        amount: Number(amount) || 0,
      }))
      .sort((a, b) => (b.amount || 0) - (a.amount || 0));
  }

  // -------------- Countries options (para el filtro de bancos) --------------

  loadCountryOptions(): void {
    this.sub.add(
      this.vita
        .listCountries({
          page: 1,
          limit: 100,
          sortBy: 'displayOrder',
          sortDir: 'ASC',
        })
        .subscribe({
          next: (res) => (this.countryOptions = res.data || []),
          error: () => (this.countryOptions = []),
        }),
    );
  }

  // -------------- Countries list --------------

  private buildCountriesQuery(): CountriesQuery {
    const q: CountriesQuery = {
      page: this.countriesPage,
      limit: this.countriesLimit,
      sortBy: 'displayOrder',
      sortDir: 'ASC',
    };
    if (this.countriesSearch) q.search = this.countriesSearch;
    if (this.countriesActive) q.isActive = this.countriesActive;
    return q;
  }

  loadCountries(): void {
    this.countriesLoading = true;
    this.countriesError = null;
    this.sub.add(
      this.vita.listCountries(this.buildCountriesQuery()).subscribe({
        next: (res) => {
          this.countries = res.data || [];
          this.countriesTotal = res.total || 0;
          this.countriesTotalPages = res.totalPages || 1;
          this.countriesPage = res.page || 1;
          this.countriesLoading = false;
        },
        error: (err) => {
          if (err?.transient) {
            this.countriesLoading = false;
            return;
          }
          this.countries = [];
          this.countriesError =
            typeof err === 'string' ? err : 'Error obteniendo países';
          this.countriesLoading = false;
        },
      }),
    );
  }

  onCountriesSearchInput(): void {
    this.countrySearch$.next(this.countriesSearch);
  }

  onCountriesFilterChange(): void {
    this.countriesPage = 1;
    this.loadCountries();
  }

  goCountriesPage(page: number): void {
    if (page < 1 || page > this.countriesTotalPages) return;
    this.countriesPage = page;
    this.loadCountries();
  }

  onCountriesPageSizeChange(): void {
    this.countriesPage = 1;
    this.loadCountries();
  }

  /**
   * Togglea `isActive` del país. Reemplaza al viejo `deleteCountry`.
   * - Si hoy está activo → confirma antes de desactivar (acción sensible).
   * - Si está inactivo → re-activa directo.
   * Nunca elimina la fila; la configuración (displayOrder, supports_*, etc)
   * se preserva siempre.
   */
  toggleCountryActive(country: VitaCountry): void {
    if (this.deletingCountryId) return;
    const willDisable = country.isActive;

    const run = () => {
      this.deletingCountryId = country.id;
      this.sub.add(
        this.vita.toggleCountryActive(country.id, !country.isActive).subscribe({
          next: (updated) => {
            this.deletingCountryId = null;
            // Actualiza in-place (ni borrar ni ocultar; queda en la lista).
            this.countries = this.countries.map((c) =>
              c.id === updated.id ? { ...c, isActive: updated.isActive } : c,
            );
            this.countryOptions = this.countryOptions.map((c) =>
              c.id === updated.id ? { ...c, isActive: updated.isActive } : c,
            );
          },
          error: (err) => {
            this.deletingCountryId = null;
            alert(
              typeof err === 'string'
                ? err
                : 'No se pudo cambiar el estado del país',
            );
          },
        }),
      );
    };

    if (!willDisable) {
      run();
      return;
    }
    const ref = this.dialog.open(ConfirmDeactivateCountryDialogComponent, {
      data: { countryName: country.name, countryCode: country.code },
      autoFocus: false,
    });
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) run();
    });
  }

  simulateWithdrawal(r: WithdrawalRow): void {
    this.dialog.open(SimulateRateDialogComponent, {
      data: {
        flowLabel: 'Retiro',
        countryCode: r.countryCode,
        destLabel: r.destLabel,
        flagIso: r.flagIso,
        sourceCurrency: r.sourceCurrency,
        destCurrency: r.destCurrency,
        sellPrice: r.sellPrice,
        fixedCost: r.fixedCost,
        fixedCostUsd: r.fixedCostUsd,
        minAmount: r.minAmount,
        businessDays: r.businessDays,
      },
      autoFocus: false,
    });
  }

  simulateVitaSent(r: VitaSentRow): void {
    this.dialog.open(SimulateRateDialogComponent, {
      data: {
        flowLabel: 'Transferencia Vita',
        countryCode: r.countryCode,
        destLabel: r.destLabel,
        flagIso: r.flagIso,
        sourceCurrency: r.sourceCurrency,
        destCurrency: r.destCurrency,
        sellPrice: r.sellPrice,
        fixedCost: r.fixedCost,
        fixedCostUsd: 0,
      },
      autoFocus: false,
    });
  }

  simulatePayin(r: PayinRow): void {
    this.dialog.open(SimulateRateDialogComponent, {
      data: {
        flowLabel: 'Payin',
        countryCode: r.countryCode,
        destLabel: r.destLabel,
        flagIso: r.flagIso,
        sourceCurrency: r.sourceCurrency,
        destCurrency: r.destinationCurrency.toUpperCase(),
        sellPrice: r.sellPrice,
        fixedCost: r.fixedCost,
        fixedCostUsd: 0,
      },
      autoFocus: false,
    });
  }

  /** Abre el modal de configuración del Rail Colombia (markup por riel COBREB/CO). */
  openRailColombiaConfig(country?: VitaCountry): void {
    this.dialog.open(RailColombiaConfigComponent, {
      data: { countryCode: country?.code ?? 'CO', countryName: country?.name ?? 'Colombia' },
      width: '900px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  /** Abre el modal de configuración del Rail Perú (markup USD→PEN). */
  openRailPeruConfig(country?: VitaCountry): void {
    this.dialog.open(RailPeruConfigComponent, {
      data: { countryCode: country?.code ?? 'PE', countryName: country?.name ?? 'Perú' },
      width: '760px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  /** Abre el modal de configuración del rail VES→PEN (pata VES→USD, pais='PE'). */
  openRailVesPenConfig(country?: VitaCountry): void {
    this.dialog.open(RailVesPenConfigComponent, {
      data: { countryCode: country?.code ?? 'PE', countryName: country?.name ?? 'Perú' },
      width: '760px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  /** Abre el modal de configuración del rail CLP→PEN (pata CLP→USD, pais='PE'). */
  openRailClpPenConfig(country?: VitaCountry): void {
    this.dialog.open(RailClpPenConfigComponent, {
      data: { countryCode: country?.code ?? 'PE', countryName: country?.name ?? 'Perú' },
      width: '760px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  /** Abre el modal de configuración del rail CLP→COP (pata CLP→USD). */
  openClpCopConfig(country?: VitaCountry): void {
    this.dialog.open(RailClpCopConfigComponent, {
      data: { countryCode: country?.code ?? 'CO', countryName: country?.name ?? 'Colombia' },
      width: '760px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  /** Abre el modal de configuración del rail VES→COP (pata VES→USD). */
  openVesCopConfig(country?: VitaCountry): void {
    this.dialog.open(RailVesCopConfigComponent, {
      data: { countryCode: country?.code ?? 'CO', countryName: country?.name ?? 'Colombia' },
      width: '760px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  /** Abre el modal "Vista cliente" (cómo vería el cliente la cotización a Colombia). */
  openClientSim(country?: VitaCountry): void {
    this.dialog.open(RailColombiaClientSimComponent, {
      data: { countryCode: country?.code ?? 'CO', countryName: country?.name ?? 'Colombia' },
      width: '460px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  /** Abre el modal "Vista cliente" del rail Perú (USD→PEN). */
  openPeruClientSim(country?: VitaCountry): void {
    this.dialog.open(RailPeruClientSimComponent, {
      data: { countryCode: country?.code ?? 'PE', countryName: country?.name ?? 'Perú' },
      width: '460px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  /**
   * Abre el conversor de saldo de la wallet master. El exchange de Vita
   * opera sobre la wallet de negocio (no acepta `wallet` param), por eso
   * solo se ofrece sobre la card MASTER. Al cerrar con movimiento, refresca
   * saldos.
   */
  openConvertDialog(w: WalletView, fromCurrency?: string): void {
    const currencies = w.balances.map((b) => b.currency);
    const from = (fromCurrency || w.balances[0]?.currency || 'CLP');
    const fromBalance =
      w.balances.find((b) => b.currency === from.toUpperCase())?.amount || 0;
    const ref = this.dialog.open(ConvertCurrencyDialogComponent, {
      data: {
        walletUuid: w.item.uuid,
        fromCurrency: from,
        fromBalance,
        currencies,
      },
      width: '660px',
      maxWidth: '95vw',
      autoFocus: false,
    });
    ref.afterClosed().subscribe((moved: boolean) => {
      if (moved) this.loadWallets();
    });
  }

  openAddCountryDialog(): void {
    const ref = this.dialog.open(AddCountryDialogComponent, {
      autoFocus: false,
    });
    ref.afterClosed().subscribe((activated: VitaCountry | undefined) => {
      if (!activated) return;
      // Refrescar listado principal y opciones de bancos
      this.loadCountries();
      const opt = this.countryOptions.find((c) => c.id === activated.id);
      if (opt) {
        opt.isActive = activated.isActive;
      } else {
        this.countryOptions.push(activated);
      }
    });
  }

  clearCountriesFilters(): void {
    this.countriesSearch = '';
    this.countriesActive = 'true';
    this.countriesPage = 1;
    this.loadCountries();
  }

  /**
   * Indica si el ISO-2 del país está en nuestra whitelist (tabla
   * vita_wallet_countries). Se usa en la tabla de tasas para marcar
   * "EN DB" sobre cada fila, diferenciando la oferta cruda de Vita
   * de lo que nosotros efectivamente soportamos.
   */
  isCountryInWhitelist(code: string | null | undefined): boolean {
    if (!code) return false;
    const upper = code.toUpperCase();
    return this.countryOptions.some((c) => c.code === upper);
  }

  // -------------- Diagnóstico Vita --------------

  openDiagnostics(): void {
    if (this.diagnosticsLoading) return;
    this.diagnosticsLoading = true;
    this.sub.add(
      this.vita.getDiagnostics().subscribe({
        next: (d: VitaDiagnostics) => {
          this.diagnosticsLoading = false;
          this.dialog.open(DiagnosticsDialogComponent, {
            data: d,
            width: '680px',
            maxWidth: '95vw',
            autoFocus: false,
          });
        },
        error: (err) => {
          this.diagnosticsLoading = false;
          alert(typeof err === 'string' ? err : 'Error obteniendo diagnóstico');
        },
      }),
    );
  }

  // -------------- Helpers --------------

  refresh(): void {
    this.loadHealth();
    this.loadWallets();
  }

  toggleSection(section: string): void {
    if (this.expandedSections.has(section)) {
      this.expandedSections.delete(section);
    } else {
      this.expandedSections.add(section);
    }
  }

  get countriesRangeLabel(): string {
    if (this.countriesTotal === 0) return '0 registros';
    const from = (this.countriesPage - 1) * this.countriesLimit + 1;
    const to = Math.min(this.countriesTotal, from + this.countries.length - 1);
    return `${from}–${to} de ${this.countriesTotal}`;
  }

  // -------------- Prices --------------

  loadPrices(force = false): void {
    if (this.pricesLoading) return;
    this.pricesLoading = true;
    this.pricesError = null;
    this.sub.add(
      this.vita.getPrices(force).subscribe({
        next: (res) => {
          this.pricesRaw = res;
          this.pricesFetchedAt = new Date();
          this.flattenPrices(res);
          this.pricesLoading = false;
        },
        error: (err) => {
          if (err?.transient) {
            this.pricesLoading = false;
            return; // silent retry next tick
          }
          this.pricesError =
            typeof err === 'string' ? err : 'Error obteniendo tasas de Vita';
          this.pricesLoading = false;
        },
      }),
    );
  }

  private flattenPrices(res: VitaPricesResponse): void {
    const withdrawal: WithdrawalRow[] = [];
    const vitasent: VitaSentRow[] = [];
    const payins: PayinRow[] = [];
    const sourceCurrencies = new Set<string>();
    const paymentMethods = new Set<string>();
    const seenW = new Set<string>();
    const seenV = new Set<string>();

    const vu = res?.withdrawal?.prices?.attributes?.valid_until;
    this.pricesValidUntil = vu ? new Date(vu) : null;

    this.extractWithdrawal(res?.withdrawal, withdrawal, sourceCurrencies, seenW);
    this.extractVitaSent(res?.vita_sent, vitasent, sourceCurrencies, seenV);

    for (const key of Object.keys(res || {})) {
      if (['payins', 'withdrawal', 'vita_sent'].includes(key)) continue;
      const section = (res as any)[key];
      if (!section || typeof section !== 'object') continue;
      if (section.withdrawal) this.extractWithdrawal(section.withdrawal, withdrawal, sourceCurrencies, seenW);
      if (section.vita_sent) this.extractVitaSent(section.vita_sent, vitasent, sourceCurrencies, seenV);
    }

    if (res?.payins) {
      for (const [country, entry] of Object.entries(res.payins)) {
        if (!entry?.payment_methods) continue;
        const cc = country.toUpperCase();
        const base = this.baseCountryCode(cc);
        const flagIso = this.isoFlagCode(base);
        const destLabel = this.destinationLabel(cc);
        for (const pm of entry.payment_methods) {
          const sellPrice = Number(pm.sell_price) || 0;
          const fixedCost = Number(pm.fixed_cost) || 0;
          const exampleAmount = this.exampleAmountForRow(entry.source_currency);
          payins.push({
            countryCode: cc,
            paymentMethod: pm.payment_method,
            sourceCurrency: entry.source_currency,
            destinationCurrency: entry.destination_currency,
            sellPrice,
            fixedCost,
            spread: Number(pm.spread) || 0,
            spreadType: pm.spread_type,
            baseCountry: base,
            flagIso,
            destLabel,
            exampleAmount,
            exampleReceive: Math.max(0, exampleAmount * sellPrice - fixedCost),
          });
          paymentMethods.add(pm.payment_method);
        }
      }
    }

    this.withdrawalRows = withdrawal.sort((a, b) =>
      a.countryCode.localeCompare(b.countryCode) || a.sourceCurrency.localeCompare(b.sourceCurrency),
    );
    this.vitaSentRows = vitasent.sort((a, b) =>
      a.countryCode.localeCompare(b.countryCode) || a.sourceCurrency.localeCompare(b.sourceCurrency),
    );
    this.payinRows = payins.sort((a, b) =>
      a.countryCode.localeCompare(b.countryCode) || a.paymentMethod.localeCompare(b.paymentMethod),
    );

    this.sourceCurrencyOptions = [...sourceCurrencies].filter((c) => c !== 'base').sort();
    this.paymentMethodOptions = [...paymentMethods].sort();

    this.applyPriceFilters();
  }

  private extractWithdrawal(
    block: any,
    out: WithdrawalRow[],
    currencies: Set<string>,
    seen: Set<string>,
  ): void {
    const attrs = block?.prices?.attributes;
    if (!attrs) return;
    const days = attrs.business_days_of_payment || {};
    const fixed = attrs.fixed_cost || {};
    const fixedUsd = attrs.fixed_cost_usd || {};
    const minAmt = attrs.min_amount || {};

    for (const key of Object.keys(attrs)) {
      if (!key.endsWith('_sell')) continue;
      const source = key.replace('_sell', '');
      const priceMap = attrs[key];
      if (!priceMap || typeof priceMap !== 'object') continue;
      currencies.add(source);
      for (const [country, price] of Object.entries(priceMap)) {
        const dedupeKey = source + '|' + country.toLowerCase();
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        const cc = country.toUpperCase();
        const base = this.baseCountryCode(cc);
        const sellPrice = Number(price) || 0;
        const fixedCost = Number(fixed[country]) || 0;
        const exampleAmount = this.exampleAmountForRow(source);
        out.push({
          sourceCurrency: source,
          countryCode: cc,
          sellPrice,
          fixedCost,
          fixedCostUsd: Number(fixedUsd[country]) || 0,
          minAmount: Number(minAmt[country]) || 0,
          businessDays: Number(days[country]) || 0,
          baseCountry: base,
          flagIso: this.isoFlagCode(base),
          destLabel: this.destinationLabel(cc),
          destCurrency: this.destinationCurrency(cc),
          exampleAmount,
          exampleReceive: Math.max(0, exampleAmount * sellPrice - fixedCost),
        });
      }
    }
  }

  private extractVitaSent(
    block: any,
    out: VitaSentRow[],
    currencies: Set<string>,
    seen: Set<string>,
  ): void {
    const attrs = block?.prices?.attributes;
    if (!attrs) return;
    const fixed = attrs.fixed_cost || {};
    for (const key of Object.keys(attrs)) {
      if (!key.endsWith('_sell')) continue;
      const source = key.replace('_sell', '');
      const priceMap = attrs[key];
      if (!priceMap || typeof priceMap !== 'object') continue;
      currencies.add(source);
      for (const [country, price] of Object.entries(priceMap)) {
        const dedupeKey = source + '|' + country.toLowerCase();
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        const cc = country.toUpperCase();
        const base = this.baseCountryCode(cc);
        const sellPrice = Number(price) || 0;
        const fixedCost = Number(fixed[country]) || 0;
        const exampleAmount = this.exampleAmountForRow(source);
        out.push({
          sourceCurrency: source,
          countryCode: cc,
          sellPrice,
          fixedCost,
          baseCountry: base,
          flagIso: this.isoFlagCode(base),
          destLabel: this.destinationLabel(cc),
          destCurrency: this.destinationCurrency(cc),
          exampleAmount,
          exampleReceive: Math.max(0, exampleAmount * sellPrice - fixedCost),
        });
      }
    }
  }

  isoFlagCode(code: string): string | null {
    if (!code || code.length !== 2) return null;
    return code.toLowerCase();
  }

  /**
   * Devuelve el ISO-2 base de una variante de destino.
   * Ej: ARUSD→AR, COCOP→CO, COBREB→CO, HKUSD→HK, GBEUR→GB.
   */
  baseCountryCode(code: string): string {
    if (!code) return code;
    const upper = code.toUpperCase();
    if (upper.length === 2) return upper;
    // 2 primeros chars si empieza con letras (ARUSD→AR, COCOP→CO)
    if (/^[A-Z]{2}/.test(upper)) return upper.substring(0, 2);
    return upper;
  }

  /** Matcheo de filtro: country base inclusivo de sus variantes. */
  matchesCountryFilter(code: string): boolean {
    if (!this.pricesCountryFilter) return true;
    return this.baseCountryCode(code) === this.pricesCountryFilter;
  }

  /** Moneda destino inferida por variante. */
  destinationCurrency(code: string): string {
    const upper = (code || '').toUpperCase();
    // Variantes con moneda explícita al final
    if (upper.endsWith('USD') || upper === 'USWIRES') return 'USD';
    if (upper.endsWith('EUR')) return 'EUR';
    if (upper.endsWith('COP')) return 'COP';
    if (upper.endsWith('BREB')) return 'COP';
    // Mapa por país base
    const byCountry: Record<string, string> = {
      CL: 'CLP', CO: 'COP', VE: 'VES', AR: 'ARS', BR: 'BRL', MX: 'MXN',
      PE: 'PEN', PY: 'PYG', UY: 'UYU', DO: 'DOP', PA: 'USD', HT: 'HTG',
      BO: 'BOB', EC: 'USD', CN: 'CNY', CR: 'CRC', ES: 'EUR', GT: 'GTQ',
      AU: 'AUD', EU: 'EUR', GB: 'GBP', US: 'USD', HK: 'HKD',
    };
    return byCountry[this.baseCountryCode(upper)] || upper;
  }

  /** Monto base del ejemplo según moneda origen (USD/EUR/GBP = 100, otras = configurable). */
  exampleAmountForRow(sourceCurrency: string): number {
    const c = (sourceCurrency || '').toLowerCase();
    if (c === 'usd' || c === 'eur' || c === 'gbp') return 100;
    return this.exampleAmount;
  }

  /** Calcula el monto recibido en destino: (amount × sell_price) − fixed_cost. */
  exampleReceive(sourceCurrency: string, sellPrice: number, fixedCost: number): number {
    const amount = this.exampleAmountForRow(sourceCurrency);
    const received = amount * (sellPrice || 0) - (fixedCost || 0);
    return received > 0 ? received : 0;
  }

  /**
   * Construye el label de una variante dinámicamente.
   * Fuente: nombre del país desde DB (countryOptions) + moneda derivada
   * del código de variante (sufijo USD/EUR/COP, o default country currency).
   * No hay maps hardcodeados país-por-país — extensible a cualquier variante
   * que Vita publique en el futuro.
   */
  destinationLabel(code: string): string {
    if (!code) return '';
    const iso = code.substring(0, 2).toUpperCase();
    const country = this.countryOptions.find((c) => c.code === iso);
    return buildVitaVariantLabel(code, {
      countryName: country?.name ?? null,
      currency: country?.currency?.toUpperCase() ?? null,
    });
  }

  refreshPrices(): void {
    this.loadPrices(true);
  }

  onPricesTabChange(tab: 'withdrawal' | 'vitasent' | 'payins'): void {
    this.pricesTab = tab;
  }

  clearPricesFilters(): void {
    this.pricesCountryFilter = '';
    this.pricesSourceCurrencyFilter = '';
    this.pricesPaymentMethodFilter = '';
    this.pricesSearch = '';
    this.applyPriceFilters();
  }

  onPricesFilterChange(): void {
    this.applyPriceFilters();
  }

  applyPriceFilters(): void {
    const q = (this.pricesSearch || '').toLowerCase();
    const countryF = this.pricesCountryFilter;
    const srcF = this.pricesSourceCurrencyFilter;
    const pmF = this.pricesPaymentMethodFilter;

    this.filteredWithdrawalRows = this.withdrawalRows.filter((r) => {
      if (countryF && r.baseCountry !== countryF) return false;
      if (srcF && r.sourceCurrency !== srcF) return false;
      if (!q) return true;
      return (
        r.countryCode.toLowerCase().includes(q) ||
        r.destLabel.toLowerCase().includes(q) ||
        r.sourceCurrency.toLowerCase().includes(q)
      );
    });

    this.filteredVitaSentRows = this.vitaSentRows.filter((r) => {
      if (countryF && r.baseCountry !== countryF) return false;
      if (srcF && r.sourceCurrency !== srcF) return false;
      if (!q) return true;
      return (
        r.countryCode.toLowerCase().includes(q) ||
        r.destLabel.toLowerCase().includes(q) ||
        r.sourceCurrency.toLowerCase().includes(q)
      );
    });

    this.filteredPayinRows = this.payinRows.filter((r) => {
      if (countryF && r.baseCountry !== countryF) return false;
      if (pmF && r.paymentMethod !== pmF) return false;
      if (!q) return true;
      return (
        r.countryCode.toLowerCase().includes(q) ||
        r.paymentMethod.toLowerCase().includes(q) ||
        r.sourceCurrency.toLowerCase().includes(q) ||
        r.destinationCurrency.toLowerCase().includes(q)
      );
    });
  }

  // trackBy functions — evitan re-render completo en cada change detection
  trackByWalletUuid = (_: number, w: WalletView) => w.item.uuid;
  trackByBalanceCurrency = (_: number, b: BalanceRow) => b.currency;
  trackByCountryId = (_: number, c: VitaCountry) => c.id;
  trackByCountryCode = (_: number, c: VitaCountry) => c.code;
  trackByString = (_: number, s: string) => s;
  trackByWithdrawalRow = (_: number, r: WithdrawalRow) =>
    r.sourceCurrency + '|' + r.countryCode;
  trackByVitaSentRow = (_: number, r: VitaSentRow) =>
    r.sourceCurrency + '|' + r.countryCode;
  trackByPayinRow = (_: number, r: PayinRow) =>
    r.countryCode + '|' + r.paymentMethod + '|' + r.sourceCurrency;

  get pricesFreshnessMinutes(): number | null {
    if (!this.pricesFetchedAt) return null;
    return Math.floor((Date.now() - this.pricesFetchedAt.getTime()) / 60000);
  }

  get pricesValidUntilClass(): 'fresh' | 'warn' | 'expired' {
    if (!this.pricesValidUntil) return 'warn';
    const minsLeft = (this.pricesValidUntil.getTime() - Date.now()) / 60000;
    if (minsLeft < 0) return 'expired';
    if (minsLeft < 3) return 'warn';
    return 'fresh';
  }

  get pricesValidUntilLabel(): string {
    if (!this.pricesValidUntil) return '—';
    const minsLeft = Math.floor((this.pricesValidUntil.getTime() - Date.now()) / 60000);
    if (minsLeft < 0) return `Expiró hace ${Math.abs(minsLeft)} min`;
    if (minsLeft === 0) return `Expira en < 1 min`;
    return `Válido por ${minsLeft} min`;
  }
}
